import { randomUUID } from 'crypto';
import fs from 'fs-extra';
import { dirname, resolve } from 'path';
import type { Database } from 'sqlite';
import { ApiItemResponse, ApiListResponse, ApiPagination } from '../types';
import { createConcurrentQueues, sleep } from '../utils';
import {
  ItemDetailsExtendedProperties,
  ListingScraperOptions,
  OnPageFetchedCallbackRes,
} from './types';
import { styleText } from 'node:util';

type TFetchedItemDetails<TItemDetail> =
  | (Partial<ApiItemResponse<TItemDetail>> &
      ItemDetailsExtendedProperties & {
        pagination: ApiPagination | null;
      })
  | null
  | undefined;

export class ListingScraper<TItemShort extends { id: string }, TItemDetail extends { id: string }> {
  private id = randomUUID();
  private startTime = new Date();
  private inMemoryItems: TItemDetail[] = [];
  private stats = {
    pages: 0,
    pagesSuccess: 0,
    items: 0,
    itemsSuccess: 0,
    requests: 0,
    requestsStartTime: new Date(),
  };
  private filePath: string;
  private db: Database | null = null;
  private tableName: string;
  private sqliteDatabaseOpenPromise: Promise<void> | null = null;
  private done = false;
  private scrapePagesDone = false;
  private error: any | null = null;
  private scrapedItems: Record<
    string,
    {
      found: boolean;
      scraped: boolean;
    }
  > = {};
  private paginationToken: string | null = null;
  private pagination: ApiPagination | null = null;
  private undefinedPagination = false;

  constructor(private options: ListingScraperOptions<TItemShort, TItemDetail>) {
    if (this.options.optionsOverride) {
      this.options = {
        ...this.options,
        ...this.options.optionsOverride,
      };
    }

    if (!this.options.outputType) {
      this.options.outputType = 'sqlite';
    }

    this.tableName = this.options.tableName || `${this.options.entityName}_${this.id}`;
    this.filePath = resolve(
      this.options.outputDir || resolve(process.cwd(), 'output'),
      this.options.filename ||
        `${this.startTime.toISOString().replace(/:/g, '-').replace(/\./g, '-')}_${
          this.options.entityName
        }_${this.id}`,
    );
  }

  log(...args: any[]) {
    if (!this.options.disableLog) {
      const onLogResult = this.options.onLog?.({ args });
      if (onLogResult?.skip) {
        return;
      }
      if (onLogResult?.overrideArgs) {
        args = onLogResult.overrideArgs;
      }
      // eslint-disable-next-line no-console
      console.log(
        `${this.options.disableLogTimestamps ? '' : `[${new Date().toISOString()}]`}`,
        ...args,
      );
    }
  }
  errorLog(...args: any[]) {
    if (!this.options.disableErrorLog) {
      const onLogResult = this.options.onLog?.({ args });
      if (onLogResult?.skip) {
        return;
      }
      if (onLogResult?.overrideArgs) {
        args = onLogResult.overrideArgs;
      }
      console.error(
        `${this.options.disableLogTimestamps ? '' : `[${new Date().toISOString()}]`}`,
        ...args,
      );
    }
  }

  private scrapePageQueue!: (args: {
    page: number;
    scrapedList?: ApiListResponse<TItemShort>;
  }) => Promise<void>;

  private fetchItemQueue!: (args: {
    item: TItemShort;
    pagination: ApiPagination | null;
  }) => Promise<TFetchedItemDetails<TItemDetail>>;

  private onItemScrapedQueue!: (
    args: { item: TItemDetail } & TFetchedItemDetails<TItemDetail>,
  ) => Promise<void>;

  async scrapeStart() {
    this.stats = {
      pages: 0,
      pagesSuccess: 0,
      items: 0,
      itemsSuccess: 0,
      requests: 0,
      requestsStartTime: new Date(),
    };
    this.paginationToken = null;
    this.pagination = null;
    this.scrapePagesDone = false;
    const startPageNumber = this.options.startPage || 1;
    const firstPage = await this.fetchPage({ page: startPageNumber });

    this.options.onFirstPageFetched?.({ data: firstPage });

    let totalPages = firstPage?.pagination?.totalPages || 0;
    this.paginationToken = firstPage?.pagination?.paginationToken || null;
    this.pagination = firstPage?.pagination || null;

    if (this.options.maxPageNumber && totalPages > this.options.maxPageNumber) {
      totalPages = this.options.maxPageNumber;
    }
    if (!totalPages && firstPage?.elements?.length) {
      totalPages = this.options.maxPageNumber;
      this.undefinedPagination = true;
    } else {
      this.undefinedPagination = false;
    }

    if (
      this.options.warnPageLimit &&
      totalPages === this.options.maxPageNumber &&
      firstPage?.pagination?.pageSize &&
      firstPage?.pagination.totalElements
    ) {
      const totalAllowedItems = this.options.maxPageNumber * firstPage.pagination.pageSize;
      const totalItems = firstPage.pagination.totalElements;

      if (totalItems > totalAllowedItems) {
        console.warn(
          '\n' +
            styleText('bgYellow', ' [WARNING] \n') +
            `The search results are limited to ${
              this.options.maxPageNumber * firstPage?.pagination?.pageSize
            } items (out of total ${
              firstPage.pagination.totalElements
            }) because LinkedIn does not allow to scrape more for one query. \n` +
            `Which means you will not be able to extract all data for this exact query. \n` +
            'Consider splitting your query into multiple queries applying more filters. \n' +
            `For example do multiple runs for locations of specific cities, instead of one run targeting entire country or region. ` +
            '\n',
        );
      }
    }

    const concurrency =
      this.options?.overrideConcurrency || firstPage?.user?.requestsConcurrency || 1;

    if (!firstPage?.elements?.length) {
      this.done = true;
      if (this.error || !Array.isArray(firstPage?.elements)) {
        const errors = Array.isArray(this.error) ? this.error : [this.error];
        if (!errors.length && firstPage?.error) {
          errors.push(firstPage.error);
        }
        this.errorLog(...errors);
        this.log('Error fetching the first page. Exiting.');
      } else {
        this.log(`No items found. Exiting.`);
      }

      return;
    }

    this.scrapePageQueue = createConcurrentQueues(
      this.options?.overridePageConcurrency || 1,
      (args) => this.scrapePage(args),
    );
    this.fetchItemQueue = createConcurrentQueues(concurrency, async ({ item, pagination }) => {
      if (this.done) return null;

      if (this.options.maxItems && this.stats.itemsSuccess + 1 > this.options.maxItems) {
        this.log(`Max items limit of ${this.options.maxItems} reached. Stopping scraping.`);
        this.done = true;
        this.error = `Max items limit reached: ${this.options.maxItems}`;
        return null;
      }

      let doneCheckInterval: NodeJS.Timeout | null = null;

      const result = await Promise.race([
        this.options
          .fetchItem({
            item,
            addHeaders: this.options.addItemHeaders,
            // sessionId: this.options.sessionId,
          })
          ?.catch((error) => {
            this.errorLog('Error scraping item', error);
            return null;
          }),
        new Promise<null>((resolve) => {
          doneCheckInterval = setInterval(() => {
            if (this.done) {
              resolve(null);
            }
          });
        }),
      ]);

      if (doneCheckInterval) {
        clearInterval(doneCheckInterval);
      }

      if (!result) return null;
      return {
        ...result,
        pagination,
      };
    });
    this.onItemScrapedQueue = createConcurrentQueues(
      this.options.outputType === 'sqlite' ? 1 : concurrency,
      ({ item, ...rest }) => this.onItemScraped({ item, ...rest }),
    );

    this.stats.requestsStartTime = new Date();
    this.stats.pages = 1;
    this.stats.pagesSuccess = 1;

    if (this.options.outputType === 'sqlite') {
      this.sqliteDatabaseOpenPromise = this.createSqliteDatabase();
    }

    let lastPageNumber = totalPages;
    if (this.options.takePages && this.options.takePages > 0) {
      lastPageNumber = Math.min(startPageNumber + this.options.takePages - 1, totalPages);
    }

    this.log(
      `Total pages: ${totalPages}. Total items: ${
        firstPage?.pagination?.totalElements || firstPage?.elements?.length || 0
      }. Scraping up to page ${lastPageNumber}${
        this.options.maxItems ? ` or until ${this.options.maxItems} items are scraped` : ''
      }.`,
    );

    const promises: Promise<void>[] = [];
    for (let page = startPageNumber; page <= lastPageNumber; page++) {
      promises.push(
        this.scrapePageQueue({
          page,
          scrapedList: page === startPageNumber ? firstPage : undefined,
        }),
      );
    }

    await Promise.all(promises);
    await this.finalize();

    this.log(
      `Finished scraping ${this.options.entityName}. Scraped pages: ${this.stats.pages}. Scraped items: ${this.stats.itemsSuccess}. Total requests: ${this.stats.requests}.`,
    );

    if (this.error) {
      const errors = Array.isArray(this.error) ? this.error : [this.error];
      this.errorLog(...errors);
    }

    return this.stats;
  }

  private async scrapePage({
    page,
    scrapedList,
  }: {
    page: number;
    scrapedList?: ApiListResponse<TItemShort>;
  }) {
    if (this.done || (this.scrapePagesDone && !scrapedList)) return;
    const list = scrapedList ? scrapedList : await this.fetchPage({ page });
    if (this.done) return;

    this.paginationToken = list?.pagination?.paginationToken || null;
    this.pagination = list?.pagination || null;

    let detailsResult: {
      details: TItemDetail[];
      skippedCounter?: number;
      keepScrapingIfAllSkippedOnPage?: boolean;
    } | null = null;

    if (list?.elements?.length) {
      detailsResult = await this.scrapePageItems({ list });
    } else {
      this.log(`No items found on page ${page}. Marking pages scraping as done.`);
      this.scrapePagesDone = true;
    }
    if (this.done) return;

    if (!detailsResult?.details?.length) {
      this.scrapePagesDone = true;

      if (detailsResult?.skippedCounter && detailsResult.keepScrapingIfAllSkippedOnPage) {
        this.scrapePagesDone = false;
      } else {
        this.log(`No items scraped from page ${page}. Marking pages scraping as done.`);
      }
    } else {
      this.scrapePagesDone = false;
    }
    // if (this.undefinedPagination) {
    //   if (!details?.length) {
    //     this.scrapePagesDone = true;
    //   } else {
    //     this.scrapePagesDone = false;
    //   }
    // }

    this.log(
      `Scraped ${this.options.entityName} page ${page}. Items found: ${detailsResult?.details.length}.`,
    );
  }

  private async fetchPage({ page }: { page: number }) {
    this.log(`Scraping page ${page} of ${this.options.entityName}...`);

    let result: ApiListResponse<TItemShort> | null = null as any;

    const runFetcher = async () => {
      result = await this.options
        .fetchList({
          page,
          paginationToken: this.paginationToken,
          sessionId: this.options.sessionId,
          addHeaders: this.options.addListingHeaders,
          ...this.options.getFetchListParams?.({
            page,
            pagination: this.pagination,
          }),
        })
        .catch((error) => {
          this.errorLog('Error fetching page', page, error);
          return null;
        });
    };

    await runFetcher();

    if (!result?.pagination || (this.pagination?.totalPages && !result?.pagination?.totalPages)) {
      this.log(`Page ${page} failed. Retrying...`);
      await sleep(5000);
      await runFetcher();
    }
    if (this.pagination?.totalPages && !result?.pagination?.totalPages) {
      this.log(`Page ${page} failed. Retrying...`);
      await sleep(5000);
      await runFetcher();
    }

    let onPageFetchedPromise:
      | Promise<OnPageFetchedCallbackRes | void>
      | OnPageFetchedCallbackRes
      | void
      | null = null;

    if (this.options.onPageFetched) {
      try {
        onPageFetchedPromise = this.options.onPageFetched({ page, data: result });
      } catch (error) {
        this.errorLog('Error in onPageFetched callback', error);
      }
      if (
        typeof (onPageFetchedPromise as Promise<OnPageFetchedCallbackRes | void>)?.catch ===
        'function'
      ) {
        (onPageFetchedPromise as Promise<OnPageFetchedCallbackRes | void>).catch((error) => {
          this.errorLog('Error in onPageFetched callback', error);
          return null;
        });
      }
    }
    const onFetchedResult = await onPageFetchedPromise;

    if (onFetchedResult?.doneAll) {
      this.log(`Marking scraping as done based on onPageFetched result.`);
      this.done = true;
    }
    if (onFetchedResult?.donePages) {
      this.log(`Marking pages scraping as done based on onPageFetched result.`);
      this.scrapePagesDone = true;
    }

    if (result?.error) {
      this.errorLog(
        `Error fetching ${this.options.entityName} page ${page}:`,
        typeof result.error === 'object' ? JSON.stringify(result.error, null, 2) : result.error,
      );
    }

    if (result?.status === 402) {
      this.log(`Request limit exceeded - please upgrade your plan. Marking scraping as done.`);
      this.done = true;
      this.error = result.error || 'Request limit exceeded - please upgrade your plan';
      return null;
    }

    if (onFetchedResult?.retryPage) {
      this.log(`Retrying page ${page} of ${this.options.entityName}...`);
      return this.fetchPage({ page });
    }

    this.stats.pages++;
    this.stats.requests++;
    if (result?.entityId) {
      this.stats.pagesSuccess++;
    }
    return result;
  }

  private async scrapePageItems({ list }: { list: ApiListResponse<TItemShort> }): Promise<{
    details: TItemDetail[];
    skippedCounter?: number;
    keepScrapingIfAllSkippedOnPage?: boolean;
  }> {
    if (!list?.elements) {
      return { details: [] };
    }
    if (this.done) {
      return { details: [] };
    }

    const details: TItemDetail[] = [];
    let skippedCounter = 0;
    let keepScrapingIfAllSkippedOnPageLocal = false;

    const itemPromises = list.elements.map(async (item) => {
      let itemDetails: TFetchedItemDetails<TItemDetail> = null;
      this.stats.items++;

      if (!item?.id || this.scrapedItems[item.id]) {
        return null;
      }
      this.scrapedItems[item.id] = { found: true, scraped: false };

      if (this.options.scrapeDetails) {
        itemDetails = await this.fetchItemQueue({ item, pagination: list.pagination });

        if (itemDetails?.status === 402) {
          this.log(`Request limit exceeded - please upgrade your plan. Marking scraping as done.`);
          this.done = true;
          this.error = itemDetails?.error || 'Request limit exceeded - please upgrade your plan';
          return null;
        }
      } else {
        itemDetails = {
          entityId: item?.id,
          element: item as any,
          status: list.status,
          error: list.error,
          query: list.query,
          pagination: list.pagination,
        };
      }

      if (this.options.scrapeDetails && !itemDetails?.skipCount && !itemDetails?.skipResult) {
        this.stats.requests++;
      }
      if (itemDetails?.done) {
        this.log(`Marking pages scraping as done based on item details for item ${item.id}.`);
        this.scrapePagesDone = true;
        this.done = true;
      }

      if (itemDetails?.keepScrapingIfAllSkippedOnPage) {
        keepScrapingIfAllSkippedOnPageLocal = true;
      }

      if (itemDetails?.skipResult) {
        skippedCounter++;
        return null;
      }

      if (itemDetails?.element && itemDetails.entityId) {
        if (this.options.maxItems && this.stats.itemsSuccess + 1 > this.options.maxItems) {
          this.log(`Max items limit of ${this.options.maxItems} reached. Stopping scraping.`);
          this.done = true;
          this.error = `Max items limit reached: ${this.options.maxItems}`;
          return null;
        }

        if (!this.scrapedItems[item.id].scraped) {
          this.scrapedItems[item.id].scraped = true;
          this.stats.itemsSuccess++;

          await this.onItemScrapedQueue({ item: itemDetails.element, ...itemDetails });
          details.push(itemDetails.element);
        }
      }
    });

    if (
      skippedCounter &&
      list.elements.length === skippedCounter &&
      !this.options.keepScrapingIfAllSkippedOnPage &&
      !keepScrapingIfAllSkippedOnPageLocal
    ) {
      this.scrapePagesDone = true;
      this.done = true;
      this.log(`All items on page were skipped. Stopping scraping as per configuration.`);
    }

    await Promise.all(itemPromises).catch((error) => {
      this.errorLog('Error scraping items', error);
    });

    if (this.options.maxItems && this.stats.itemsSuccess + 1 > this.options.maxItems) {
      this.log(`Max items limit of ${this.options.maxItems} reached. Stopping scraping.`);
      this.done = true;
      this.error = `Max items limit reached: ${this.options.maxItems}`;
    }

    return {
      details,
      skippedCounter,
      keepScrapingIfAllSkippedOnPage:
        this.options.keepScrapingIfAllSkippedOnPage || keepScrapingIfAllSkippedOnPageLocal,
    };
  }

  private onItemScraped = async ({
    item,
    ...apiArgs
  }: { item: TItemDetail } & TFetchedItemDetails<TItemDetail>) => {
    const logger = {
      log: (...args: any[]) => this.log(...args),
      error: (...args: any[]) => this.errorLog(...args),
    };
    if (this.options.outputType === 'json') {
      this.inMemoryItems.push(item);
      void this.options.onItemScraped?.({ item, logger, ...apiArgs });
    }
    if (this.options.outputType === 'sqlite') {
      await this.insertSqliteItem(item).catch((error) => {
        this.errorLog('Error inserting item to SQLite:', error);
      });
      void this.options.onItemScraped?.({ item, logger, ...apiArgs });
    }
    if (this.options.outputType === 'callback') {
      await this.options.onItemScraped?.({ item, logger, ...apiArgs });
    }
  };

  private async createSqliteDatabase() {
    try {
      const open = require('sqlite').open; // eslint-disable-line @typescript-eslint/no-require-imports
      const sqlite3 = require('sqlite3'); // eslint-disable-line @typescript-eslint/no-require-imports

      await fs.ensureDir(dirname(this.filePath));

      this.db = await open({
        filename: `${this.filePath}.sqlite`,
        driver: sqlite3.Database,
      });

      await this.db!.exec(
        `CREATE TABLE IF NOT EXISTS "${this.tableName}" (db_id INTEGER PRIMARY KEY AUTOINCREMENT)`,
      );
    } catch (error) {
      this.error = ['Error creating SQLite database:', error];
      this.errorLog(...this.error);
      this.done = true;
    }
  }

  private async insertSqliteItem(item: TItemDetail) {
    await this.sqliteDatabaseOpenPromise;

    const existingColumns = await this.db!.all(`PRAGMA table_info("${this.tableName}")`);
    const existingColumnNames = existingColumns.map((col) => col.name);

    for (const key of Object.keys(item as any)) {
      if (!existingColumnNames.includes(key)) {
        await this.db!.exec(`ALTER TABLE "${this.tableName}" ADD COLUMN "${key}" TEXT`);
      }
    }

    const keys = Object.keys(item as any)
      .map((key) => key)
      .map((key) => `"${key}"`);

    const insertSQL = `INSERT INTO "${this.tableName}" (${keys.join(', ')}) VALUES (${keys
      .map(() => '?')
      .join(', ')})`;

    await this.db!.run(
      insertSQL,
      Object.values(item as any).map((value) =>
        typeof value === 'object' ? JSON.stringify(value) : String(value),
      ),
    );
  }

  private async finalize() {
    if (this.options.outputType === 'json') {
      fs.outputJson(
        `${this.filePath}.json`,
        {
          stats: this.stats,
          list: this.inMemoryItems,
        },
        { spaces: 2 },
      );
    }

    if (this.db) {
      await this.db.close();
    }
  }
}
